import { Gift, LayoutDashboard, LogOut, Moon, Printer, Sparkles, Sun, User } from 'lucide-react';
import type React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Navbar: React.FC = () => {
  const location = useLocation();
  const { user, loading, logout, setShowAuthModal } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const isActive = (path: string) =>
    location.pathname === path
      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient shadow-lg shadow-purple-500/30'
      : 'nav-link-inactive';

  return (
    <nav
      className="glass-dark sticky top-0 z-50"
      style={{ borderBottom: '1px solid var(--border-color)' }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
              <div className="relative bg-gradient-to-r from-blue-500 to-purple-500 p-2.5 rounded-xl">
                <Printer className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex items-center">
              <span className="font-black text-2xl text-gradient-animated">Printy</span>
              <Sparkles className="w-4 h-4 text-yellow-400 ml-1 animate-pulse" />
            </div>
          </Link>

          {/* Navigation Links */}
          <div className="flex items-center space-x-2">
            <Link
              to="/"
              className={`flex items-center px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ${isActive('/')}`}
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Order Now
            </Link>
            <Link
              to="/spin"
              className={`flex items-center px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ${isActive('/spin')}`}
            >
              <Gift className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Win Prizes!</span>
              <span className="sm:hidden">Spin</span>
            </Link>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl nav-link-inactive transition-all"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Auth section */}
            <div className="ml-2 pl-4 border-l" style={{ borderColor: 'var(--border-color)' }}>
              {loading ? (
                <div
                  className="w-8 h-8 rounded-full animate-pulse"
                  style={{ backgroundColor: 'var(--surface-inactive)' }}
                />
              ) : user ? (
                <div className="flex items-center gap-2">
                  <Link
                    to="/account"
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all ${
                      isActive('/account').includes('from-blue-500')
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                        : 'surface-inactive hover:surface-inactive-hover'
                    }`}
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <span
                      className="text-sm font-medium hidden sm:inline"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {user.name}
                      {user.isGuest && (
                        <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
                          (Guest)
                        </span>
                      )}
                    </span>
                  </Link>
                  <button
                    onClick={logout}
                    className="p-2 rounded-xl transition-all hover:bg-red-500/10 hover:text-red-600"
                    style={{ color: 'var(--text-muted)' }}
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign In</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
